/**
 * The engine's sound, in Web Audio — the {@link AudioBackend} a browser uses.
 *
 * THE SIGNAL PATH:
 *
 *   decoded track ─▶ source gain ─▶ level ─▶ fade ─▶ ┌ music  ─▶ duck ┐
 *                                                     ├ battle ─▶ duck ┤
 *                                                     └ vehicle ───────┼▶ master ─▶ analyser ─▶ out
 *   decoded cue ─▶ gain ────────────────────────▶ alerts / voice / vehicle ┘
 *
 * THE VEHICLE BUS IS THE VEHICLE LAYER'S AND THE VEHICLE CUES', and it is never ducked: the duck makes room
 * for an alert in the music, and the sound of the car a player sits in is not music — nothing mutes it but
 * its own slider and master. Its decks and its cues meet at the same slider gain, so one slider moves both.
 *
 * EVERY TRACK IS A DECODED BUFFER, BECAUSE A LOOP IS THE WHOLE FILE. The library's loops have no loop
 * points: the last sample flows into the first (plan §9.7), and only an AudioBufferSourceNode with `loop`
 * plays that seam sample-accurately. A media element's `loop` seeks back to the top and leaves a gap at
 * every repeat, and MP3's encoder padding turns the gap into a click. The cost is memory — a decoded
 * minute of 48 kHz stereo is about 23 MB — so a decoded track is kept while a deck plays it, plus a small
 * budget of recent ones ({@link DECODED_BUDGET_BYTES}); the compressed file stays in the service worker's
 * cache, and a later visit decodes it again from disk.
 *
 * WHAT A BUFFER BUYS BESIDES THE SEAM. A seek is `start(when, offset)` — sample-accurate and immediate,
 * which is what a shared instance (§9.9) asks of it; the playback rate is an AudioParam on the audio
 * thread; and a position is arithmetic on the audio clock rather than a media element's estimate. Every
 * fade and duck is a gain ramp on the audio thread, which a hidden tab cannot throttle.
 *
 * WHERE THE LISTENER IS. `positionMs()` is what is being HEARD now, not what is being rendered: the audio
 * clock is mapped onto `performance.now()` with `getOutputTimestamp()`, which accounts for the output
 * latency (tens of milliseconds; a quarter of a second over Bluetooth). `play` starts that much further
 * in, so two members of an instance on two devices meet on one beat at the speaker, not at the mixer.
 *
 * AUTOPLAY (§6.1). Nothing here makes a sound before {@link WebAudioBackend.resume} runs inside the "Enable
 * audio" click, and resuming the AudioContext inside that gesture is the whole unlock — Safari included,
 * since no media element is ever played. On iOS the page also asks for the `playback` audio session, so
 * the ring/silent switch does not mute it.
 */
import { TrackMissingError, type AudioBackend, type AudioBus, type Deck, type Duck } from './engine';

type Bus = 'master' | AudioBus;

/** Decoded cues kept. A cue is a second of audio; the longest one-shot, the victory sting, is 4.5 MB decoded. */
const CUE_CACHE = 16;
/**
 * Decoded tracks kept beyond the ones playing, by size: about two typical tracks. Walking back into the
 * district just left is instant; anything older decodes again out of the service worker's cache.
 */
export const DECODED_BUDGET_BYTES = 96 * 1024 * 1024;
/** How long a fetch may take before the deck gives up and the engine says so. */
const LOAD_TIMEOUT_MS = 30_000;
/** A seek crossfades the old source into the new one over this long, so a re-seek is never a click. */
const SEEK_CROSSFADE_S = 0.04;
/** The tick in a visible tab. A hidden tab's timers run about once a second, which still holds the beat. */
const TICK_MS = 250;

interface DecodedTrack {
  readonly buffer: AudioBuffer;
  readonly bytes: number;
  users: number;
  lastUsed: number;
}

export interface WebAudioBackendOptions {
  /** Injected by tests; the browser's `fetch` otherwise (and so, in production, the service worker). */
  readonly fetchImpl?: typeof fetch;
}

export class WebAudioBackend implements AudioBackend {
  private context: AudioContext | null = null;
  private readonly buses = new Map<Bus, GainNode>();
  private readonly ducks = new Map<'music' | 'battle', GainNode>();
  private readonly pendingGains = new Map<Bus, number>();
  private analyserNode: AnalyserNode | null = null;
  private readonly tracks = new Map<string, DecodedTrack>();
  private readonly decoding = new Map<string, Promise<AudioBuffer>>();
  private readonly cues = new Map<string, AudioBuffer>();
  private readonly fetchImpl: typeof fetch;
  /** The audio-clock instant the current duck's hold ends, and how deep it is — so a blip cannot cut a sting's duck short. */
  private duckHold: { until: number; depth: number; releaseS: number } | null = null;
  private watchingVisibility = false;

  constructor(options: WebAudioBackendOptions = {}) {
    this.fetchImpl = options.fetchImpl ?? ((input, init) => globalThis.fetch(input, init));
  }

  /** Whether this browser can play anything at all. */
  static supported(): boolean {
    return typeof globalThis.AudioContext === 'function';
  }

  get analyser(): AnalyserNode | null {
    return this.analyserNode;
  }

  get state(): AudioContextState | 'unstarted' {
    return this.context?.state ?? 'unstarted';
  }

  async resume(): Promise<void> {
    const context = this.ensureContext();
    // iOS (Safari 16.4+): the media-playback session, which the ring/silent switch does not mute.
    const session = (globalThis.navigator as { audioSession?: { type: string } } | undefined)?.audioSession;
    if (session !== undefined) {
      try {
        session.type = 'playback';
      } catch {
        /* a browser that refuses: the switch then applies, as it does to every page */
      }
    }
    await context.resume();
    if (!this.watchingVisibility) {
      this.watchingVisibility = true;
      // A hidden tab can come back suspended on some platforms; resume when it is looked at again.
      globalThis.document?.addEventListener('visibilitychange', () => {
        if (globalThis.document.visibilityState === 'visible' && this.context?.state === 'suspended') void this.context.resume();
      });
    }
  }

  createDeck(bus: Exclude<AudioBus, 'alerts'>): Deck {
    const context = this.ensureContext();
    const level = context.createGain();
    const fade = context.createGain();
    level.connect(fade);
    fade.connect(this.busInput(bus));
    return new BufferDeck(context, level, fade, this);
  }

  async playOneShot(url: string, bus: 'alerts' | 'voice' | 'vehicle', level: number): Promise<void> {
    const context = this.ensureContext();
    let buffer = this.cues.get(url);
    if (buffer === undefined) {
      buffer = await this.fetchAndDecode(url);
      this.cues.set(url, buffer);
      if (this.cues.size > CUE_CACHE) this.cues.delete(this.cues.keys().next().value as string);
    }
    const source = context.createBufferSource();
    const gain = context.createGain();
    source.buffer = buffer;
    gain.gain.value = level;
    source.connect(gain);
    gain.connect(this.busInput(bus));
    source.addEventListener('ended', () => {
      source.disconnect();
      gain.disconnect();
    });
    source.start();
  }

  setBusGain(bus: Bus, gain: number): void {
    const node = this.buses.get(bus);
    if (node === undefined || this.context === null) {
      this.pendingGains.set(bus, gain);
      return;
    }
    ramp(node.gain, this.context.currentTime, gain, 0.08);
  }

  /**
   * Dips the music and battle buses. A duck that lands while another is still holding joins it — the
   * deeper of the two, held until the later of the two ends — so a mail chime during the victory sting
   * does not bring the music back up halfway through the fanfare.
   */
  duck(duck: Duck): void {
    const context = this.context;
    if (context === null) return;
    const at = context.currentTime;
    let until = at + duck.holdMs / 1000;
    let depth = duck.depth;
    let releaseS = duck.releaseMs / 1000;
    const held = this.duckHold;
    if (held !== null && held.until > at) {
      until = Math.max(until, held.until);
      depth = Math.min(depth, held.depth);
      releaseS = Math.max(releaseS, held.releaseS);
    }
    this.duckHold = { until, depth, releaseS };
    for (const node of this.ducks.values()) {
      const param = node.gain;
      param.cancelScheduledValues(at);
      param.setValueAtTime(param.value, at);
      param.linearRampToValueAtTime(depth, at + 0.06);
      param.setValueAtTime(depth, Math.max(until, at + 0.06));
      param.linearRampToValueAtTime(1, Math.max(until, at + 0.06) + releaseS);
    }
  }

  dispose(): void {
    this.tracks.clear();
    this.decoding.clear();
    this.cues.clear();
    void this.context?.close();
    this.context = null;
  }

  // ----------------------------------------------------------------- shared by decks

  /** A whole track, decoded, through the cache. Released with {@link releaseTrack}. */
  async acquireTrack(url: string): Promise<AudioBuffer> {
    const cached = this.tracks.get(url);
    if (cached !== undefined) {
      cached.users += 1;
      cached.lastUsed = Date.now();
      return cached.buffer;
    }
    let pending = this.decoding.get(url);
    if (pending === undefined) {
      pending = this.fetchAndDecode(url).finally(() => this.decoding.delete(url));
      this.decoding.set(url, pending);
    }
    const buffer = await pending;
    const entry = this.tracks.get(url);
    if (entry !== undefined) {
      // Two decks asked at once and the other one landed first.
      entry.users += 1;
      entry.lastUsed = Date.now();
      return entry.buffer;
    }
    this.tracks.set(url, { buffer, bytes: buffer.length * buffer.numberOfChannels * 4, users: 1, lastUsed: Date.now() });
    this.trimTracks();
    return buffer;
  }

  releaseTrack(url: string): void {
    const cached = this.tracks.get(url);
    if (cached !== undefined) {
      cached.users = Math.max(0, cached.users - 1);
      cached.lastUsed = Date.now();
    }
    this.trimTracks();
  }

  /** Forgets the oldest idle tracks until the idle ones fit the budget. A track a deck is playing is never dropped. */
  private trimTracks(): void {
    const idle = [...this.tracks.entries()].filter(([, track]) => track.users === 0).sort((a, b) => a[1].lastUsed - b[1].lastUsed);
    let bytes = idle.reduce((sum, [, track]) => sum + track.bytes, 0);
    while (bytes > DECODED_BUDGET_BYTES && idle.length > 0) {
      const [url, track] = idle.shift() as [string, DecodedTrack];
      this.tracks.delete(url);
      bytes -= track.bytes;
    }
  }

  private async fetchAndDecode(url: string): Promise<AudioBuffer> {
    const context = this.ensureContext();
    const response = await withTimeout(this.fetchImpl(url), LOAD_TIMEOUT_MS, `${url} did not arrive in time`);
    if (response.status === 404) throw new TrackMissingError(url);
    if (!response.ok) throw new Error(`${url} answered ${response.status}`);
    const bytes = await response.arrayBuffer();
    return new Promise<AudioBuffer>((resolve, reject) => {
      // Both forms: the callbacks for an older Safari, and a catch on the promise newer browsers return
      // as well, so a decode failure is reported once rather than also as an unhandled rejection.
      const failed = (): void => reject(new Error(`${url} could not be decoded by this browser`));
      const returned = context.decodeAudioData(bytes, resolve, failed) as Promise<AudioBuffer> | undefined;
      returned?.catch(failed);
    });
  }

  private ensureContext(): AudioContext {
    if (this.context !== null) return this.context;
    // `playback` asks for larger buffers: more latency, which music does not mind (and `positionMs`
    // accounts for), and less CPU and battery for a tab that plays for hours in the background.
    const context = new AudioContext({ latencyHint: 'playback' });
    const master = context.createGain();
    const analyser = context.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.78;
    master.connect(analyser);
    analyser.connect(context.destination);
    this.buses.set('master', master);
    for (const bus of ['music', 'battle'] as const) {
      const gain = context.createGain();
      const duck = context.createGain();
      gain.connect(duck);
      duck.connect(master);
      this.buses.set(bus, gain);
      this.ducks.set(bus, duck);
    }
    // Straight into master, with no duck: the duck is the music's way of making room, and none of these is music.
    for (const bus of ['vehicle', 'alerts', 'voice'] as const) {
      const gain = context.createGain();
      gain.connect(master);
      this.buses.set(bus, gain);
    }
    for (const [bus, gain] of this.pendingGains) {
      const node = this.buses.get(bus);
      if (node !== undefined) node.gain.value = gain;
    }
    this.pendingGains.clear();
    this.analyserNode = analyser;
    this.context = context;
    return context;
  }

  private busInput(bus: AudioBus): GainNode {
    return this.buses.get(bus) as GainNode;
  }
}

/** Ramps a param from where it is to `to`, on the audio clock. */
function ramp(param: AudioParam, at: number, to: number, seconds: number): void {
  param.cancelScheduledValues(at);
  param.setValueAtTime(param.value, at);
  if (seconds <= 0) param.setValueAtTime(to, at);
  else param.linearRampToValueAtTime(to, at + seconds);
}

/** Points per second of an equal-power ramp: a 20 ms step is far finer than any ear follows a fade. */
const EQUAL_POWER_STEPS_PER_S = 50;

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

/**
 * Moves a FADE gain (0..1) along the equal-power curve, gain = sin θ, with θ moving in a straight line
 * from `from` to `to`. A fade-in from 0 and a fade-out from 1 over the same span are then sin and cos of
 * the same angle, so their POWERS sum to one at every instant — two unrelated pieces of music crossfade
 * without the ~3 dB hollow a linear crossfade leaves at its middle, and without the abrupt-sounding ends a
 * linear gain has (the ear hears gain logarithmically).
 *
 * Scheduled as fine linear segments after a hold at `from`, rather than one `setValueCurveAtTime`,
 * because a value curve may not overlap any other automation and a fade is often asked for in the middle
 * of another one: this shape can always be scheduled, and never jumps. `from` is the caller's to know
 * (see {@link GainAutomation}) — never `param.value`.
 */
export function equalPowerRamp(param: AudioParam, at: number, from: number, to: number, seconds: number): void {
  const start = clamp01(from);
  const target = clamp01(to);
  param.cancelScheduledValues(at);
  param.setValueAtTime(start, at);
  if (seconds <= 0 || Math.abs(target - start) < 1e-6) {
    param.setValueAtTime(target, at);
    return;
  }
  const a = Math.asin(start);
  const b = Math.asin(target);
  const steps = Math.min(512, Math.max(8, Math.ceil(seconds * EQUAL_POWER_STEPS_PER_S)));
  for (let step = 1; step <= steps; step += 1) {
    const share = step / steps;
    param.linearRampToValueAtTime(Math.sin(a + (b - a) * share), at + seconds * share);
  }
}

/**
 * One gain's automation AS THIS CODE SCHEDULED IT: where it was, where it is going, from when and over how
 * long — so where it is now is computed, never read back from `AudioParam.value`.
 *
 * Reading it back was a measured defect. Chrome updates `value` only when the audio thread renders the
 * node, and a deck with nothing playing into it yet is not rendered (silence propagates past it), so a
 * deck reset to 0 and asked to fade in within the same moment — every track that comes from the decoded
 * cache, which is every return to an area just left and every battle's end — read the default 1, and its
 * "fade" ran from 1 to 1: the track came in at full volume over the music still fading out.
 */
export class GainAutomation {
  private from: number;
  private to: number;
  private at = 0;
  private seconds = 0;

  constructor(
    private readonly curve: 'equal-power' | 'linear',
    initial: number,
  ) {
    this.from = initial;
    this.to = initial;
  }

  /** Where the gain is at audio time `t`, by the curve that was scheduled. */
  valueAt(t: number): number {
    if (this.seconds <= 0 || t >= this.at + this.seconds) return this.to;
    if (t <= this.at) return this.from;
    const share = (t - this.at) / this.seconds;
    if (this.curve === 'linear') return this.from + (this.to - this.from) * share;
    const a = Math.asin(clamp01(this.from));
    const b = Math.asin(clamp01(this.to));
    return Math.sin(a + (b - a) * share);
  }

  /** The value the gain is heading to (or at). */
  target(): number {
    return this.to;
  }

  /**
   * Moves `param` from wherever it is now to `to` over `seconds`. Asking for the target it is already
   * heading to changes nothing (a resync mid-fade leaves the curve alone) and returns false.
   */
  moveTo(param: AudioParam, to: number, at: number, seconds: number): boolean {
    const target = this.curve === 'equal-power' ? clamp01(to) : to;
    if (Math.abs(target - this.to) < 1e-9) return false;
    const from = this.valueAt(at);
    this.from = from;
    this.to = target;
    this.at = at;
    this.seconds = Math.max(0, seconds);
    if (this.curve === 'equal-power') {
      equalPowerRamp(param, at, from, target, this.seconds);
      return true;
    }
    param.cancelScheduledValues(at);
    param.setValueAtTime(from, at);
    if (this.seconds > 0) param.linearRampToValueAtTime(target, at + this.seconds);
    else param.setValueAtTime(target, at);
    return true;
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error instanceof Error ? error : new Error(String(error)));
      },
    );
  });
}

/**
 * Where a source is in its buffer, as a piecewise-linear function of the audio clock: one segment per
 * start and per rate change. `position` is UNWRAPPED (it keeps counting past the end of a loop); reading
 * it wraps it.
 */
interface Segment {
  /** Audio-clock seconds the segment begins at. */
  readonly at: number;
  /** Buffer seconds rendered at `at`, unwrapped. */
  readonly position: number;
  readonly rate: number;
}

/** One playing track: a decoded buffer, its source, and the arithmetic that says where it is. */
class BufferDeck implements Deck {
  private readonly context: AudioContext;
  private readonly level: GainNode;
  private readonly fadeGain: GainNode;
  private readonly backend: WebAudioBackend;
  private buffer: AudioBuffer | null = null;
  private url: string | null = null;
  private source: AudioBufferSourceNode | null = null;
  private sourceGain: GainNode | null = null;
  private looping = false;
  private rate = 1;
  private segments: Segment[] = [];
  /** The fade and level gains as scheduled: where each is now is computed from these (see GainAutomation). */
  private readonly fadeMove = new GainAutomation('equal-power', 0);
  private readonly levelMove = new GainAutomation('linear', 1);
  private endedListener: (() => void) | null = null;
  private tickListener: (() => void) | null = null;
  private ticker: ReturnType<typeof setInterval> | null = null;
  private disposed = false;

  constructor(context: AudioContext, level: GainNode, fadeGain: GainNode, backend: WebAudioBackend) {
    this.context = context;
    this.level = level;
    this.fadeGain = fadeGain;
    this.backend = backend;
    // A new deck is silent until it is faded in: the node's own default is 1, the model's start is 0.
    fadeGain.gain.value = 0;
  }

  get durationMs(): number | null {
    return this.buffer === null ? null : this.buffer.duration * 1000;
  }

  async load(url: string): Promise<void> {
    const buffer = await this.backend.acquireTrack(url);
    if (this.disposed) {
      this.backend.releaseTrack(url);
      return;
    }
    this.buffer = buffer;
    this.url = url;
  }

  async play(offsetMs: number, loop: boolean): Promise<void> {
    if (this.buffer === null || this.disposed) return;
    this.looping = loop;
    this.rate = 1;
    this.startSource(offsetMs / 1000, 0);
    if (this.ticker === null) {
      this.ticker = setInterval(() => {
        if (!this.disposed) this.tickListener?.();
      }, TICK_MS);
    }
  }

  positionMs(): number {
    return this.positionAt(this.heardNow()) * 1000;
  }

  seek(offsetMs: number): void {
    if (this.buffer === null || this.source === null || this.disposed) return;
    this.startSource(offsetMs / 1000, SEEK_CROSSFADE_S);
  }

  setRate(rate: number): void {
    const source = this.source;
    if (source === null || Math.abs(rate - this.rate) < 1e-4) return;
    const at = this.context.currentTime;
    this.segments.push({ at, position: this.unwrappedAt(at), rate });
    this.rate = rate;
    source.playbackRate.setValueAtTime(rate, at);
    this.trimSegments();
  }

  setLevel(level: number, rampMs = 0): void {
    this.levelMove.moveTo(this.level.gain, level, this.context.currentTime, rampMs / 1000);
  }

  fade(to: number, ms: number): void {
    this.fadeMove.moveTo(this.fadeGain.gain, to, this.context.currentTime, ms / 1000);
  }

  onEnded(listener: () => void): void {
    this.endedListener = listener;
  }

  onTick(listener: () => void): void {
    this.tickListener = listener;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.ticker !== null) clearInterval(this.ticker);
    this.ticker = null;
    this.stopSource(this.source, this.sourceGain, 0);
    this.source = null;
    this.sourceGain = null;
    this.level.disconnect();
    this.fadeGain.disconnect();
    if (this.url !== null) this.backend.releaseTrack(this.url);
    this.buffer = null;
  }

  // ----------------------------------------------------------------- the arithmetic

  /**
   * A new source, so that `heardS` is what the listener hears now: it starts `latency` further in,
   * because what is rendered now is heard that much later. Crossfades out of the old one for a seek.
   */
  private startSource(heardS: number, crossfadeS: number): void {
    const buffer = this.buffer as AudioBuffer;
    const at = this.context.currentTime;
    const rendered = heardS + this.latencyS() * this.rate;
    const duration = buffer.duration;
    const previous = this.source;
    const previousGain = this.sourceGain;

    this.segments = [{ at, position: rendered, rate: this.rate }];
    if (!this.looping && rendered >= duration) {
      // Past the end already (a `once` score joined late): nothing to play, and the end has happened.
      this.stopSource(previous, previousGain, crossfadeS);
      this.source = null;
      this.sourceGain = null;
      queueMicrotask(() => {
        if (!this.disposed) this.endedListener?.();
      });
      return;
    }

    const source = this.context.createBufferSource();
    const gain = this.context.createGain();
    source.buffer = buffer;
    source.loop = this.looping;
    source.playbackRate.value = this.rate;
    source.connect(gain);
    gain.connect(this.level);
    if (crossfadeS > 0) {
      gain.gain.setValueAtTime(0, at);
      gain.gain.linearRampToValueAtTime(1, at + crossfadeS);
    }
    source.onended = () => {
      if (source === this.source && !this.disposed && !this.looping) this.endedListener?.();
    };
    const offset = this.looping ? ((rendered % duration) + duration) % duration : Math.max(0, rendered);
    source.start(at, offset);
    this.source = source;
    this.sourceGain = gain;
    this.stopSource(previous, previousGain, crossfadeS);
  }

  private stopSource(source: AudioBufferSourceNode | null, gain: GainNode | null, fadeS: number): void {
    if (source === null) return;
    source.onended = null;
    const at = this.context.currentTime;
    try {
      if (fadeS > 0 && gain !== null) {
        ramp(gain.gain, at, 0, fadeS);
        source.stop(at + fadeS + 0.01);
      } else {
        source.stop();
      }
    } catch {
      /* never started, or already stopped */
    }
    const release = (): void => {
      source.disconnect();
      gain?.disconnect();
    };
    if (fadeS > 0) setTimeout(release, (fadeS + 0.1) * 1000);
    else release();
  }

  /** The audio-clock instant being heard right now. */
  private heardNow(): number {
    const context = this.context;
    const stamp = typeof context.getOutputTimestamp === 'function' ? context.getOutputTimestamp() : null;
    if (stamp?.contextTime !== undefined && stamp.performanceTime !== undefined && stamp.performanceTime > 0) {
      const heard = stamp.contextTime + (globalThis.performance.now() - stamp.performanceTime) / 1000;
      return Math.min(heard, context.currentTime);
    }
    return Math.max(0, context.currentTime - (context.outputLatency || context.baseLatency || 0));
  }

  /** How far the rendered audio is ahead of the heard audio, in seconds. */
  private latencyS(): number {
    return Math.max(0, this.context.currentTime - this.heardNow());
  }

  private unwrappedAt(at: number): number {
    const segments = this.segments;
    if (segments.length === 0) return 0;
    let segment = segments[0] as Segment;
    for (const candidate of segments) {
      if (candidate.at <= at) segment = candidate;
      else break;
    }
    return segment.position + (at - segment.at) * segment.rate;
  }

  /** Buffer seconds at an audio-clock instant: wrapped for a loop, clamped for a track that ends. */
  private positionAt(at: number): number {
    const position = this.unwrappedAt(at);
    const duration = this.buffer?.duration ?? 0;
    if (duration <= 0) return Math.max(0, position);
    if (this.looping) return ((position % duration) + duration) % duration;
    return Math.min(Math.max(position, 0), duration);
  }

  /** Keeps the segments still needed to read the heard position (it trails the rendered one by the latency). */
  private trimSegments(): void {
    const horizon = this.heardNow() - 1;
    while (this.segments.length > 1 && (this.segments[1] as Segment).at <= horizon) this.segments.shift();
  }
}
